<template>
  <v-container fluid>
    <v-card class="mt-4 pa-4">
      <div v-if="!isView" class="alert-message d-flex align-center">
        <v-icon color="warning" class="mr-2">mdi-alert-circle</v-icon>
        <span>Os campos marcados com asterisco(*) são obrigatórios</span>
      </div>
      <v-toolbar flat color="primary" dark class="mt-4">
        <v-icon class="mr-2">mdi-pencil</v-icon>
        Acompanhamento da Turma {{ isView ? '- Visualizar' : '' }}
        <v-spacer />
      </v-toolbar>
      <v-row class="mt-3">
        <v-col cols="6" v-for="(campo, i) in camposFixos" :key="i">
          <v-text-field :label="campo.label" :value="campo.valor" disabled outlined dense />
        </v-col>
      </v-row>
      <v-tabs :value="bimestreSelecionado">
        <v-tab v-for="(b, i) in bimestres" :key="i" @click.prevent="handleTabClick(i)">{{ b.nome }}</v-tab>
      </v-tabs>
      <v-tabs-items v-model="bimestreSelecionado">
        <v-tab-item v-for="(b, i) in bimestres" :key="i">
          <v-expansion-panels v-model="painelAtivo[i]" multiple>
            <v-expansion-panel v-for="(grupo, gIndex) in b.grupos" :key="gIndex" class="mb-4">
              <v-expansion-panel-header class="font-weight-bold text-subtitle-1" :class="{'red--text': grupo.itens.some(i => i.erro)}">
                <h3 class="mb-0" style="justify-content: center; text-align: center;">
                  {{ grupo.nome }}
                  <v-icon v-if="grupo.itens.some(i => i.erro)" color="red" class="mr-2">mdi-alert-circle</v-icon>
                </h3>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-4" :ref="`grupo-panel-${i}-${gIndex}`" eager>
                <div class="d-flex justify-end mb-4" v-if="grupo.isPraticaLinguagem && !isView">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="abrirDialogo(grupo, 2)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Incluir Práticas de Linguagem</span>
                  </v-tooltip>
                </div>
                <div class="d-flex justify-end mb-4" v-if="grupo.isUnidadeTematica && !isView">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="abrirDialogo(grupo, 1)">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Incluir Unidade Temática</span>
                  </v-tooltip>
                </div>
                <v-row class="font-weight-bold text--darken-2 mb-2" v-if="grupo.cabecalhos">
                  <v-col v-for="(cab, hIndex) in grupo.cabecalhos" :key="hIndex" :cols="cab.cols || 4">
                    {{ cab.label }}
                  </v-col>
                </v-row>
                <div v-for="(item, idx) in grupo.itens" :key="idx" style="border-bottom: 1px solid #e0e0e0; padding: 12px 0">
                  <v-row>
                    <template v-if="grupo.isPraticaLinguagem || grupo.isUnidadeTematica">
                      <v-col cols="2">{{ item.pratica }}</v-col>
                      <v-col cols="2">{{ item.codigo }}</v-col>
                      <v-col cols="1.5">
                        <v-text-field
                          :ref="`input-${i}-${gIndex}-${idx}-nao`"
                          v-model.number="item.qtdNao"
                          type="number"
                          outlined
                          dense
                          class="no-spin"
                          :disabled="isView"
                          :rules="[(v) => v === 0 || !!v || 'Campo obrigatório']"
                          @input="validarTotais(grupo)" />
                      </v-col>
                      <v-col cols="1.5" class="text-center">{{ item.percNao }}%</v-col>
                      <v-col cols="1.5">
                        <v-text-field
                          v-model.number="item.qtdParcial"
                          type="number"
                          outlined
                          dense
                          class="no-spin"
                          :disabled="isView"
                          :rules="[(v) => v === 0 || !!v || 'Campo obrigatório']"
                          @input="validarTotais(grupo)" />
                      </v-col>
                      <v-col cols="1.5" class="text-center">{{ item.percParcial }}%</v-col>
                      <v-col cols="1.5">
                        <v-text-field
                          v-model.number="item.qtdTotal"
                          type="number"
                          outlined
                          dense
                          class="no-spin"
                          :disabled="isView"
                          :rules="[(v) => v === 0 || !!v || 'Campo obrigatório']"
                          @input="validarTotais(grupo)" />
                      </v-col>
                      <v-col cols="1.5" class="text-center">{{ item.percTotal }}%</v-col>
                    </template>
                    <template v-else>
                      <v-col :cols="grupo.cabecalhos?.[0]?.cols || 4">{{ item.descricao }}</v-col>
                      <v-col :cols="grupo.cabecalhos?.[2]?.cols || 4">
                        <v-text-field
                          :ref="`input-${i}-${gIndex}-${idx}`"
                          v-model.number="item.qtd"
                          class="no-spin"
                          type="number"
                          outlined
                          dense
                          @input="onIntegerInput(item, 'qtd', grupo)"
                          :disabled="isView"
                          :rules="[(v) => v === 0 || !!v || 'Campo obrigatório']"
                          :class="{'erro-campo': item.erro}"
                        />
                      </v-col>
                      <v-col :cols="grupo.cabecalhos?.[2]?.cols || 4">
                        <div class="text-subtitle-2 text-right pr-2 text-center">{{ item.percentual }}%</div>
                      </v-col>
                    </template>
                  </v-row>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>
      </v-tabs-items>
      <div v-if="!isView" class="d-flex justify-end mt-4">
        <sca-buttons @click="salvar()" @cancel="voltar()" :actionText="'Salvar'" />
      </div>
      <div v-else class="btn-group text-center">
        <v-btn color="primary" @click="voltar">
          <v-icon>mdi-close</v-icon>
          Fechar
        </v-btn>
      </div>
    </v-card>
    <v-dialog v-model="mostrarDialogo" max-width="500">
      <v-card class="not-margin pt-0 pr-0 pl-0">
        <v-toolbar dark color="primary">
          <div class="text-center">
            {{ tipoSelecionado === 2 ? "Práticas de Linguagem" : "Unidade Temática" }}
          </div>
        </v-toolbar>
        <v-card-text class="pt-0 mt-6">
          <v-autocomplete
            v-model="praticaSelecionada"
            :items="praticasDisponiveis"
            item-text="descricao"
            item-value="praticaId"
            :label="tipoSelecionado === 2 ? 'Prática de Linguagem' : 'Unidade Temática'"
            outlined
            dense
            @change="carregarCodigos"
            :rules="[(v) => !!v || 'Campo obrigatório']"
          />
          <v-autocomplete
            v-model="codigoSelecionado"
            :items="codigosDisponiveis"
            item-text="codigoHabilidade"
            item-value="habilidadeId"
            label="Código da Habilidade"
            outlined
            dense
            :disabled="!praticaSelecionada"
            :rules="[(v) => !!v || 'Campo obrigatório']"
          />
        </v-card-text>
        <div class="d-flex justify-end mt-4">
          <sca-buttons @click="confirmarInclusao()" @cancel="mostrarDialogo = false" :actionText="'Salvar'" />
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import AcompanhamentoDeTurmasService from '~/services/acompanhamento-de-turmas.service';

export default {
  name: 'EditarAcompanhamentoDeTurmas',
  mixins: [AcompanhamentoDeTurmasService],
  data() {
    return {
      isView: this.$route.params.isView || false,
      bimestreSelecionado: 0,
      bimestreAnterior: 0,
      praticasUnidades: [],
      mostrarDialogo: false,
      grupoSelecionado: null,
      tipoSelecionado: null,
      praticaSelecionada: null,
      codigoSelecionado: null,
      praticasDisponiveis: [],
      codigosDisponiveis: [],
      originalBimestres: [],
      edit: true,
      camposFixos: [],
      painelAtivo: [[], [], [], []],
      bimestres: [
        { nome: '1º Bimestre', grupos: [] },
        { nome: '2º Bimestre', grupos: [] },
        { nome: '3º Bimestre', grupos: [] },
        { nome: '4º Bimestre', grupos: [] }
      ]
    };
  },
  methods: {
    wait(ms) { return new Promise(r => setTimeout(r, ms)); },
    bimestreFoiModificado(indice) {
      const atual = this.bimestres[indice];
      const original = this.originalBimestres[indice];
      if (!atual || !original) return false;
      return JSON.stringify(atual.grupos) !== JSON.stringify(original.grupos);
    },
    async handleTabClick(index) {
      const anterior = this.bimestreSelecionado;
      if (index === anterior) return;
      if (this.isView || !this.bimestreFoiModificado(anterior)) {
        this.bimestreSelecionado = index;
        return;
      }
      try {
        const confirmed = await this.$swal({
          text: 'Você possui informações não salvas neste bimestre. Se continuar, os dados inseridos serão perdidos. Deseja continuar?',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continuar',
          cancelButtonText: 'Permanecer',
          allowOutsideClick: false
        });
        if (confirmed) {
          this.$set(this.bimestres, anterior, JSON.parse(JSON.stringify(this.originalBimestres[anterior])));
          const grupos = this.bimestres[anterior]?.grupos || [];
          grupos.forEach(grupo => {
            grupo.itens.forEach(item => item.erro = false);
            this.atualizarPercentuais(grupo);
          });
          this.bimestreSelecionado = index;
        }
      } catch (e) {
        this.$set(this, 'bimestreSelecionado', null);
        this.$nextTick(() => {
          this.$set(this, 'bimestreSelecionado', anterior);
        });
      }
    },
    onIntegerInput(item, field, grupo) {
      const value = item[field];
      if (!Number.isInteger(value)) {
        item[field] = Math.floor(value) || 0;
      }
      this.atualizarPercentuais(grupo);
    },
    getValorCampo(label) {
      return this.camposFixos.find(c => c.label === label)?.valor;
    },
    async irParaGrupoEFocar({ bimestreIndex, grupoIndex, itemIndex, praticaOuUnidade = false }) {
      this.bimestreSelecionado = bimestreIndex;

      const abertos = this.painelAtivo[bimestreIndex] || [];
      if (!abertos.includes(grupoIndex)) {
        this.$set(this.painelAtivo, bimestreIndex, [...abertos, grupoIndex]);
      }

      await this.$nextTick();
      await this.wait(50);

      const grupoEl = this.$refs[`grupo-panel-${bimestreIndex}-${grupoIndex}`];
      const el = Array.isArray(grupoEl) ? (grupoEl[0]?.$el || grupoEl[0]) : (grupoEl?.$el || grupoEl);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      const refBase = praticaOuUnidade
        ? `input-${bimestreIndex}-${grupoIndex}-${itemIndex}-nao`
        : `input-${bimestreIndex}-${grupoIndex}-${itemIndex}`;

      const tryFocus = () => {
        const r = this.$refs[refBase];
        const comp = Array.isArray(r) ? r[0] : r;
        if (!comp) return false;

        if (typeof comp.focus === 'function') {
          comp.focus();
          comp.select && comp.select();
          return true;
        }

        const input = comp.$el?.querySelector?.('input, textarea');
        if (input) {
          input.focus();
          input.select?.();
          return true;
        }
        return false;
      };

      if (tryFocus()) return;

      await this.$nextTick();
      await this.wait(50);

      if (tryFocus()) return;

      await this.wait(150);
      tryFocus();
    },
    salvar() {
      const bimestre = this.bimestres[this.bimestreSelecionado];
      const totalEstudantes = this.getValorCampo('Quantidade de Estudantes Matriculados') || 0;
      const mensagens = {
        leitura: 'A soma da quantidade de estudantes informados em Perfil da Turma em Leitura deve ser igual à quantidade de estudantes matriculados na turma.',
        escrita: 'A soma da quantidade de estudantes informados em Perfil da Turma em Escrita deve ser igual à quantidade de estudantes matriculados na turma.',
        pratica: 'A soma da quantidade de estudantes informados em cada prática de linguagem deve ser igual à quantidade de estudantes matriculados na turma.',
        unidade: 'A soma da quantidade de estudantes informados em cada unidade temática deve ser igual à quantidade de estudantes matriculados na turma.'
      };
      const isCampoVazio = (grupo, item) => {
        if (grupo.isPraticaLinguagem || grupo.isUnidadeTematica) {
          return [item.qtdNao, item.qtdParcial, item.qtdTotal].some(v => v === null || v === undefined || v === '');
        } else {
          return item.qtd === null || item.qtd === undefined || item.qtd === '';
        }
      };
      for (let gIndex = 0; gIndex < bimestre.grupos.length; gIndex++) {
        const grupo = bimestre.grupos[gIndex];
        const algumCampoVazioIdx = grupo.itens.findIndex(item => isCampoVazio(grupo, item));
        if (algumCampoVazioIdx !== -1) {
          this.$notifyWarning('Todos os campos obrigatórios devem ser preenchidos.');
          this.irParaGrupoEFocar({
            bimestreIndex: this.bimestreSelecionado,
            grupoIndex: gIndex,
            itemIndex: algumCampoVazioIdx,
            praticaOuUnidade: !!(grupo.isPraticaLinguagem || grupo.isUnidadeTematica)
          });
          return;
        }
        if (grupo.isPraticaLinguagem || grupo.isUnidadeTematica) {
          const itemInvalidoIdx = grupo.itens.findIndex(item =>
            (item.qtdNao || 0) + (item.qtdParcial || 0) + (item.qtdTotal || 0) !== totalEstudantes
          );
          if (itemInvalidoIdx !== -1) {
            const msg = grupo.isPraticaLinguagem ? mensagens.pratica : mensagens.unidade;
            this.$notifyError(msg);
            this.irParaGrupoEFocar({
              bimestreIndex: this.bimestreSelecionado,
              grupoIndex: gIndex,
              itemIndex: itemInvalidoIdx,
              praticaOuUnidade: true
            });
            return;
          }
        } else {
          const somaGrupo = grupo.itens.reduce((sum, item) => sum + (item.qtd || 0), 0);
          if (somaGrupo !== totalEstudantes) {
            if (grupo.nome.includes('Leitura')) {
              this.$notifyError(mensagens.leitura);
            } else if (grupo.nome.includes('Escrita')) {
              this.$notifyError(mensagens.escrita);
            } else {
              this.$notifyError(`A soma da quantidade de estudantes informados em "${grupo.nome}" deve ser igual à quantidade de estudantes matriculados na turma.`);
            }
            const primeiroIdxComErro = grupo.itens.findIndex(i => i.erro);
            this.irParaGrupoEFocar({
              bimestreIndex: this.bimestreSelecionado,
              grupoIndex: gIndex,
              itemIndex: primeiroIdxComErro === -1 ? 0 : primeiroIdxComErro,
              praticaOuUnidade: false
            });
            return;
          }
        }
      }
      this.$swal({
        text: `Deseja salvar as informações do bimestre selecionado?`,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: `Confirmar`,
        cancelButtonText: `Cancelar`
      }).then((result) => {
        if (!result) return;
        const accordions = bimestre.grupos.map(grupo => {
          const praticas = grupo.itens.filter(i => i.praticaId && i.habilidadeId).map(i => ({
            id: i.id || null,
            descricao: i.pratica,
            codigoHabilidade: i.codigo,
            praticaId: i.praticaId,
            habilidadeId: i.habilidadeId,
            quantidadeNaoDesenvolveu: i.qtdNao,
            quantidadeDesenvolveuParcialmente: i.qtdParcial,
            quantidadeDesenvolveu: i.qtdTotal
          }));
          const perfis = grupo.itens.filter(i => i.perfilTurmaId).map(i => ({
            id: i.id || null,
            descricao: i.descricao,
            perfilTurmaId: i.perfilTurmaId,
            quantidadeDeEstudantes: i.qtd
          }));
          return {
            nomeAccordion: grupo.nome,
            itensPraticas: praticas,
            itensPerfil: perfis
          };
        });
        const payload = {
          id: this.$route.params.id,
          bimestre: {
            bimestre: this.bimestreSelecionado + 1,
            acompanhamentoId: bimestre.acompanhamentoId || null,
            accordions
          }
        };
        this.$editar(payload).then(() => {
          this.$notifySuccess("Registro adicionado com sucesso!");
          this.voltar();
        });
      });
    },
    atualizarPercentuais(grupo) {
      const totalTurma = this.camposFixos.find(c => c.label === 'Quantidade de Estudantes Matriculados')?.valor || 0;
      grupo.itens.forEach(item => {
        if (grupo.isPraticaLinguagem || grupo.isUnidadeTematica) {
          item.percNao = totalTurma > 0 ? ((item.qtdNao / totalTurma) * 100).toFixed(1) : 0;
          item.percParcial = totalTurma > 0 ? ((item.qtdParcial / totalTurma) * 100).toFixed(1) : 0;
          item.percTotal = totalTurma > 0 ? ((item.qtdTotal / totalTurma) * 100).toFixed(1) : 0;
        } else {
          const soma = grupo.itens.reduce((acc, i) => acc + (i.qtd || 0), 0);
          if (soma > totalTurma) {
            this.$notifyError(`A soma dos estudantes não pode ultrapassar ${totalTurma}`);
            item.erro = true;
          } else {
            item.percentual = totalTurma > 0 ? ((item.qtd / totalTurma) * 100).toFixed(1) : 0;
            item.erro = false;
          }
        }
      });
    },
    validarTotais(grupo) {
      const totalTurma = this.getValorCampo('Quantidade de Estudantes Matriculados') || 0;
      if (grupo.isPraticaLinguagem || grupo.isUnidadeTematica) {
        grupo.itens.forEach(item => {
          const totalItem = (item.qtdNao || 0) + (item.qtdParcial || 0) + (item.qtdTotal || 0);
          item.erro = totalItem !== totalTurma;
          item.percNao = totalTurma > 0 ? ((item.qtdNao / totalTurma) * 100).toFixed(1) : 0;
          item.percParcial = totalTurma > 0 ? ((item.qtdParcial / totalTurma) * 100).toFixed(1) : 0;
          item.percTotal = totalTurma > 0 ? ((item.qtdTotal / totalTurma) * 100).toFixed(1) : 0;
        });
      } else {
        const somaTotal = grupo.itens.reduce((acc, item) => acc + (item.qtd || 0), 0);
        grupo.itens.forEach(item => {
          item.erro = somaTotal !== totalTurma;
          item.percentual = totalTurma > 0 ? ((item.qtd / totalTurma) * 100).toFixed(1) : 0;
        });
      }
    },
    voltar() {
      this.$router.push({ name: "Acompanhamento de Turmas - Professor" });
    },
    abrirDialogo(grupo, tipo) {
      this.grupoSelecionado = grupo;
      this.tipoSelecionado = tipo;
      this.praticaSelecionada = null;
      this.codigoSelecionado = null;
      this.praticasDisponiveis = [];
      this.codigosDisponiveis = [];
      this.mostrarDialogo = true;
      this.$listarPraticasUnidades({tipo: tipo }).then(({ data }) => {
        this.praticasDisponiveis = data.lista;
      });
    },
    carregarCodigos() {
      const pratica = this.praticasDisponiveis.find(p => p.praticaId === this.praticaSelecionada);
      this.codigosDisponiveis = pratica?.habilidades || [];
      this.codigoSelecionado = null;
    },
    confirmarInclusao() {
      const grupo = this.grupoSelecionado;
      if (!this.praticaSelecionada) {
        const msg = this.tipoSelecionado === 2 ? 'Você precisa selecionar uma prática de linguagem' : ' Você precisa selecionar uma Unidade Temática';
        this.$notifyWarning(msg);
        return;
      }
      if (!this.codigoSelecionado) {
        const msg = this.tipoSelecionado === 2 ? 'Você precisa selecionar uma habilidade para a prática de linguagem escolhida.' : 'Você precisa selecionar uma habilidade para a unidade temática escolhida.';
        this.$notifyWarning(msg);
        return;
      }
      const duplicado = grupo.itens.some(i => i.praticaId === this.praticaSelecionada && i.habilidadeId === this.codigoSelecionado);
      if (duplicado) {
        const msg = this.tipoSelecionado === 2 ? 'Cada Prática de Linguagem deve estar associada a um Código de Habilidade único. Verifique os registros duplicados.' : 'Cada Unidade Temática deve estar associada a um Código de Habilidade único. Verifique os registros duplicados.';
        this.$notifyWarning(msg);
        return;
      }
      const praticaObj = this.praticasDisponiveis.find(p => p.praticaId === this.praticaSelecionada);
      const habilidadeObj = praticaObj?.habilidades.find(h => h.habilidadeId === this.codigoSelecionado);
      grupo.itens.push({
        praticaId: this.praticaSelecionada,
        habilidadeId: this.codigoSelecionado,
        pratica: praticaObj?.descricao,
        codigo: habilidadeObj?.codigoHabilidade,
        qtdNao: 0,
        percNao: 0,
        qtdParcial: 0,
        percParcial: 0,
        qtdTotal: 0,
        percTotal: 0
      });
      this.mostrarDialogo = false;
    }
  },
  mounted() {
    this.hidePageLoading();
    const id = this.$route.params.id;
    this.$visualizar(id).then(({ data }) => {
      const resposta = data.resposta;
      this.camposFixos = [
        { label: 'Escola', valor: resposta.escola },
        { label: 'Município', valor: resposta.municipio },
        { label: 'GRE', valor: resposta.gre },
        { label: 'Nome da Turma', valor: resposta.nomeDaTurma },
        { label: 'Ano/Série', valor: resposta.anoSerie },
        { label: 'Quantidade de Estudantes Matriculados', valor: resposta.quantidadeDeEstudantesMatriculados },
        { label: 'Componente Curricular', valor: resposta.componenteCurricular }
      ];
      resposta.bimestres.forEach((bimestre, index) => {
        const grupos = bimestre.accordions.map(accordion => {
          let cabecalhos = [];
          let itens = [];
          if (accordion.isPerfilLeitura || accordion.isPerfilEscrita) {
            cabecalhos = [
              { label: 'Perfil', cols: 6 },
              { label: 'Quantidade de estudantes', cols: 3 },
              { label: 'Percentual de estudantes em relação à turma', cols: 3 }
            ];
            itens = accordion.itens.map(item => ({
              id: item.id || null,
              descricao: item.descricao,
              qtd: item.quantidadeDeEstudantes,
              perfilTurmaId: item.perfilTurmaId,
              percentual: 0
            }));
          } else {
            cabecalhos = [
              { label: accordion.isUnidadeTematica ? 'Unidade Temática' : 'Prática de Linguagem', cols: 2 },
              { label: 'Código de Habilidade', cols: 2 },
              { label: 'Não desenvolveu', cols: 1.5 },
              { label: 'Percentual', cols: 1 },
              { label: 'Desenvolveu parcialmente', cols: 1.5 },
              { label: 'Percentual', cols: 1.5 },
              { label: 'Desenvolveu', cols: 1.5 },
              { label: 'Percentual', cols: 1.5 }
            ];
            itens = accordion.itens.map(item => ({
              id: item.id || null,
              praticaId: item.praticaId || null,
              habilidadeId: item.habilidadeId || null,
              pratica: item.descricao || '',
              codigo: item.codigoHabilidade || '',
              qtdNao: item.quantidadeNaoDesenvolveu || 0,
              percNao: 0,
              qtdParcial: item.quantidadeDesenvolveuParcialmente || 0,
              percParcial: 0,
              qtdTotal: item.quantidadeDesenvolveu || 0,
              percTotal: 0
            }));
          }
          return {
            nome: accordion.nomeAccordion,
            isPraticaLinguagem: accordion.isPraticaLinguagem,
            isUnidadeTematica: accordion.isUnidadeTematica,
            cabecalhos,
            itens
          };
        });
        if (this.bimestres[index]) {
          this.bimestres[index].grupos = grupos;
          this.bimestres[index].acompanhamentoId = bimestre.acompanhamentoId || null;
          grupos.forEach(grupo => this.atualizarPercentuais(grupo));
        }
      });
      this.originalBimestres = JSON.parse(JSON.stringify(this.bimestres));
    });
  }
};
</script>

<style>
.no-spin input[type=number]::-webkit-inner-spin-button,
.no-spin input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-spin input[type=number] {
  -moz-appearance: textfield;
}
.erro-campo .v-input__control {
  border-color: red !important;
}
.swal2-actions {
  flex-direction: row !important;
  justify-content: center !important;
}
</style>
